import { flatMap, identity } from 'lodash-es';
import useSWR from 'swr';

import { Country } from '../../models/Country';
import { COUNTRY_CODES_MAP } from '../constants';
import { getFeatureEnabledForCountry } from '../featureEnabledForCountry';
const SUPPORTED_COUNTRIES = Object.values(COUNTRY_CODES_MAP);

export type CountryCallingCodeOption = {
  countryCode: string;
  countryName: string;
  callingCode: string;
  label: string;
  isFeatured: boolean;
};

interface UseCountryCallingCodeOptionsProps {
  filterInput?: string;
  isWhatsAppInput?: boolean;
}

export const useCountryCallingCodeOptions = ({
  filterInput = '',
  isWhatsAppInput = false,
}: UseCountryCallingCodeOptionsProps = {}) => {
  const { data, isLoading, isValidating } = useSWR<{ data: Country[] }>(
    '/countries'
  );
  const normalizedFilterInput = filterInput.trim().toLowerCase();
  const countryCodeOptionsAreLoading = isLoading || isValidating;
  const countries = data?.data;

  const filteredCountries = (countries ?? []).filter(country => {
    const isSupportedCountryCode = SUPPORTED_COUNTRIES.includes(country.code);
    if (!isSupportedCountryCode) {
      return false;
    }

    if (
      isWhatsAppInput &&
      !getFeatureEnabledForCountry(country.code).isWhatsappEnabled
    ) {
      return false;
    }

    if (!normalizedFilterInput) {
      return true;
    }

    return (
      country.name.toLowerCase().includes(normalizedFilterInput) ||
      country.code.toLowerCase().includes(normalizedFilterInput) ||
      country.callingCode?.some(callingCode =>
        callingCode.includes(normalizedFilterInput)
      )
    );
  });

  const callingCodes = filteredCountries
    ? flatMap(
        filteredCountries,
        country =>
          country.callingCode?.map(callingCode => ({
            countryName: country.name,
            countryCode: country.code,
            code: callingCode,
          }))
      ).filter(identity)
    : [];

  const callingCodeOptions: CountryCallingCodeOption[] = callingCodes.map(
    callingCode => ({
      countryCode: callingCode.countryCode,
      countryName: callingCode.countryName,
      label: callingCode.countryName,
      callingCode: callingCode.code,
      isFeatured: false,
    })
  );

  return { callingCodeOptions, countryCodeOptionsAreLoading } as const;
};
